<template>
  <b-container fluid class="edit-society px-5 mt-4">
    <div class="float-right">
      <b-button @click="cancel">Back</b-button>
    </div>
    <h2>Edit Society: View {{ (society || {}).url_code }} Members</h2>
    <div v-if="society" class="mt-3 mb-3">
      <statistics :id="society.id" />
    </div>
    <members-list ref="membersList" :societyId="$route.params.societyId" />

    <v-wait for="uploading">
      <HalfCircleSpinner slot="waiting" />
      <template slot="waiting">
        <div class="spinner-message my-3 text-center">Uploading...</div>
      </template>
      <b-row class="mt-4">
        <b-col sm="12">
          <b-form @submit.prevent>
            <b-form-group
              horizontal
              label-cols="3"
              :invalid-feedback="invalidFeedback.membersFile"
              label="Members file:"
              label-class="font-weight-bold"
            >
              <b-form-file
                v-model="$v.membersFile.$model"
                @change="onFileChange"
                :state="validateState('membersFile')"
                placeholder="Choose a file or drop it here..."
                drop-placeholder="Drop file here..."
                accept=".csv"
              />
              <div class="mt-3">
                Selected file: {{ membersFile ? membersFile.name : '' }}
              </div>
              <b-button class="mt-3" variant="info" @click="sendFile">
                Upload File
              </b-button>
            </b-form-group>
          </b-form>
        </b-col>
      </b-row>
    </v-wait>
    <b-row v-if="isDemoMode">
      <b-col sm="9" :offset-sm="3" class="pl-sm-2">
        <b-button
          variant="v2_primary"
          @click="isCreateDemoUserModalOpened = true"
        >
          Create Demo User
        </b-button>
      </b-col>
    </b-row>
    <div v-if="csvUploadErrors">
      <SocietyCSVUploadErrors
        :errors="csvUploadErrors"
        v-on:dismiss="csvUploadErrors = null"
      />
    </div>
    <div v-if="csvUploadResults">
      <SocietyCSVUploadResults
        :results="csvUploadResults"
        v-on:dismiss="csvUploadResults = null"
      />
    </div>
    <modal-re-init-modal-behaviour v-if="society">
      <template slot-scope="{ keyId, keyUpdater }">
        <b-modal
          :visible="isCreateDemoUserModalOpened"
          hide-footer
          title="Create Demo User"
          @show="keyUpdater"
        >
          <b-card class="border-0">
            <b-card-body>
              <create-demo-member
                :key="keyId"
                :society-id="society.id"
                @cancel="isCreateDemoUserModalOpened = false"
                @created="onNewMemberCreated"
              />
            </b-card-body>
          </b-card>
        </b-modal>
      </template>
    </modal-re-init-modal-behaviour>
  </b-container>
</template>

<script>
import Api from '@/providers/Api.js';
import { HalfCircleSpinner } from 'epic-spinners';
import { validationMixin } from 'vuelidate';
import { fileExtensionValidator } from '@/helpers/customValidators';
import SocietyCSVUploadErrors from '@/components/society/csv/SocietyCSVUploadErrors';
import SocietyCSVUploadResults from '@/components/society/csv/SocietyCSVUploadResults';
import MembersList from '@/components/society/MembersList';
import Statistics from '@/components/society/Statistics';
import ModalReInitModalBehaviour from '@/behaviours/ForceUpdateBehaviour';
import CreateDemoMember from '@/components/society/edit-members/CreateDemoMember';

const ALLOWED_EXTENSIONS = ['csv'];

export default {
  name: 'EditMembers',
  components: {
    SocietyCSVUploadErrors,
    SocietyCSVUploadResults,
    HalfCircleSpinner,
    MembersList,
    Statistics,
    ModalReInitModalBehaviour,
    CreateDemoMember,
  },
  mixins: [validationMixin],
  data: function () {
    return {
      society: null,
      membersFile: null,
      csvUploadErrors: null,
      csvUploadResults: null,
      isCreateDemoUserModalOpened: false,
    };
  },
  validations: {
    membersFile: {
      fileExtensionValidator: fileExtensionValidator(ALLOWED_EXTENSIONS),
    },
  },
  created: function () {
    Api.getSociety(this, this.$route.params.societyId).then((s) => {
      this.society = s;
    });
  },
  computed: {
    invalidFeedback() {
      return {
        membersFile: `Only next file extensions are allowed: ${ALLOWED_EXTENSIONS.join()}`,
      };
    },
    isDemoMode: function () {
      return this.$store.getters.isDemoMode;
    },
  },
  methods: {
    cancel() {
      this.$router.go(-1);
    },
    sendFile: async function () {
      this.$v.$touch();
      if (this.$v.$anyError) {
        return;
      }
      this.removeCSVErrorsAndResults();
      if (this.membersFile) {
        this.$wait.start('uploading');
      }
      const data = {
        id: this.$route.params.societyId,
        membersFile: this.membersFile,
      };
      try {
        const updatedData = await Api.updateSocietyMembers(data);
        await this.$refs.membersList.refreshData();
        this.$toasted.success('CSV uploaded successfully', { duration: 3000 });
        this.csvUploadResults =
          updatedData && updatedData.uploadResults
            ? updatedData.uploadResults
            : null;
      } catch (error) {
        this.$toasted.error('CSV Upload error', { duration: 3000 });
        if (
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          this.csvUploadErrors = error.response.data.errors;
        } else if (process.env.NODE_ENV !== 'production') {
          console.error(error);
        }
      } finally {
        this.$wait.end('uploading');
      }
    },
    validateState(name) {
      const { $dirty, $error } = this.$v[name];
      return $dirty ? !$error : null;
    },
    removeCSVErrorsAndResults() {
      this.csvUploadErrors = null;
      this.csvUploadResults = null;
    },
    onFileChange() {
      this.$v.membersFile.$touch();
      this.removeCSVErrorsAndResults();
    },
    onNewMemberCreated: function (member) {
      if (
        this.$refs.membersList &&
        typeof this.$refs.membersList.addMemberToList === 'function'
      ) {
        this.$refs.membersList.addMemberToList(member);
      }
    },
  },
};
</script>

<style></style>
